/* Portfolio revenue: net Stripe collections per product, last 7/30 days vs the period before. Products with no
   billing data are named in the footer; their gaps get rows in the Data coverage widget. */
import type { inferRouterOutputs } from "@trpc/server"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_DAILY_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TRevenue = inferRouterOutputs<AppRouter>["billingAnalytics"]["revenueWindows"]
type TProduct = TRevenue["products"][number]
type TCurrencyRow = TProduct["currencies"][number]

/** Every product that earns or should earn money; billing product keys not listed here are appended. */
const PORTFOLIO: { key: string; name: string }[] = [
  { key: "vidmax", name: "VidMax" },
  { key: "lunchbreak", name: "Lunchbreak" },
  { key: "pebble", name: "Pebble" },
  { key: "lumi", name: "Lumi" },
  { key: "recoverme", name: "RecoverMe" },
  { key: "saas-engine", name: "SaaS Engine" },
]

const DAY_MS = 86_400_000
const MINOR_PER_MAJOR = 100
/** Week-over-week drop worth a look, and one worth acting on */
const DROP_WATCH = -0.15
const DROP_ACT = -0.4
/** No paid invoice for this long means the data stopped, not the business */
const STALE_DAYS = 3
const FAILED_SYNC = new Set(["failed", "error"])

const money = (minor: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(minor / MINOR_PER_MAJOR)

const change = (current: number, previous: number): number | null =>
  previous > 0 ? (current - previous) / previous : null

const pct = (ratio: number | null) => {
  if (ratio === null) return "new"
  const rounded = Math.round(ratio * 100)
  return `${rounded > 0 ? "+" : ""}${rounded}%`
}

/** USD first, then the largest; minor currencies show in details */
const primaryCurrency = (product: TProduct): TCurrencyRow | undefined =>
  product.currencies.slice().sort((a, b) => {
    if (a.currency === "usd") return -1
    if (b.currency === "usd") return 1
    return b.last30 - a.last30
  })[0]

const productState = (product: TProduct, now: number): { verdict: EVerdict; pill: string } => {
  if (product.sources.some((s) => s.enabled && FAILED_SYNC.has(s.lastSyncStatus ?? "")))
    return { verdict: EVerdict.WATCH, pill: "sync failed" }
  const latest = product.latestPaidAt ? Date.parse(product.latestPaidAt) : 0
  if (now - latest > STALE_DAYS * DAY_MS) return { verdict: EVerdict.WATCH, pill: "stale" }
  const main = primaryCurrency(product)
  const weekly = main ? change(main.last7, main.prev7) : null
  if (weekly !== null && weekly <= DROP_ACT) return { verdict: EVerdict.ACT, pill: pct(weekly) }
  if (weekly !== null && weekly <= DROP_WATCH) return { verdict: EVerdict.WATCH, pill: pct(weekly) }
  return { verdict: EVerdict.CLEAR, pill: weekly === null ? "new" : pct(weekly) }
}

const freshness = (product: TProduct, now: number): string => {
  const synced = product.sources.map((s) => s.lastSyncedAt).filter((at): at is string => !!at)
  if (synced.length) return `synced ${ago(synced.sort().at(-1), now)}`
  return product.latestPaidAt ? `data ends ${ago(product.latestPaidAt, now)}` : "no recent data"
}

const productRow = (key: string, name: string, product: TProduct, now: number): IWidgetRow => {
  const { verdict, pill } = productState(product, now)
  const main = primaryCurrency(product)
  const sub = main
    ? `${money(main.last7, main.currency)} 7d (${pct(change(main.last7, main.prev7))}) · ${money(main.last30, main.currency)} 30d (${pct(change(main.last30, main.prev30))})`
    : "Nothing collected in 60 days"
  const failed = product.sources.find((s) => FAILED_SYNC.has(s.lastSyncStatus ?? ""))
  return {
    key,
    company: key,
    verdict,
    pill: { text: pill, verdict },
    title: name,
    sub,
    meta: freshness(product, now),
    details: (
      <KeyValues
        pairs={[
          ...product.currencies.map((c): [string, string] => [
            c.currency.toUpperCase(),
            `7d ${money(c.last7, c.currency)} vs ${money(c.prev7, c.currency)} · 30d ${money(c.last30, c.currency)} vs ${money(c.prev30, c.currency)}`,
          ]),
          ["Latest payment", product.latestPaidAt ? ago(product.latestPaidAt, now) : "none in 60 days"],
          [
            "Sources",
            product.sources.map((s) => `${s.displayName} (${s.lastSyncStatus ?? "never synced"})`).join(", "),
          ],
          ["Sync error", failed?.lastSyncError ?? null],
          ["Counts", "Net Stripe collections: paid invoices − refunds, by payment date. Not MRR."],
        ]}
      />
    ),
  }
}

const renderRevenue = (data: TRevenue): IWidgetResult => {
  const now = Date.now()
  const byKey = new Map(data.products.map((p) => [p.productKey, p]))
  const extra = data.products
    .filter((p) => !PORTFOLIO.some((item) => item.key === p.productKey))
    .map((p) => ({ key: p.productKey, name: p.productKey }))
  const all = [...PORTFOLIO, ...extra]
  // Only products with billing data get rows; the gaps live in the Data coverage widget
  const rows = all.flatMap(({ key, name }) => {
    const product = byKey.get(key)
    return product ? [productRow(key, name, product, now)] : []
  })
  const missing = all.filter(({ key }) => !byKey.has(key)).map(({ name }) => name)

  const usd = data.products.flatMap((p) => p.currencies.filter((c) => c.currency === "usd"))
  const total7 = usd.reduce((sum, c) => sum + c.last7, 0)
  const prev7 = usd.reduce((sum, c) => sum + c.prev7, 0)
  const total30 = usd.reduce((sum, c) => sum + c.last30, 0)
  const prev30 = usd.reduce((sum, c) => sum + c.prev30, 0)

  return {
    verdict: worstVerdict(rows.filter((r) => !r.quiet).map((r) => r.verdict)),
    count: `${rows.length} of ${all.length} connected`,
    lead: (
      <KeyValues
        pairs={[
          ["Last 7d", `${money(total7, "usd")} (${pct(change(total7, prev7))} vs prior 7d)`],
          ["Last 30d", `${money(total30, "usd")} (${pct(change(total30, prev30))} vs prior 30d)`],
        ]}
      />
    ),
    rows,
    foot: missing.length
      ? `Totals cover connected products only. Not connected: ${missing.join(", ")} (see Data coverage)`
      : undefined,
  }
}

export const revenueWidget: IWidgetDefinition = {
  id: "revenue",
  title: "Portfolio revenue",
  description: "Net Stripe collections per product, 7d and 30d vs the period before",
  columns: { 2: 1, 3: 2, 4: 2, 5: 2, 6: 2 },
  useWidget: () =>
    useQueryWidget(trpc.billingAnalytics.revenueWindows.useQuery(undefined, WIDGET_DAILY_QUERY_OPTIONS), renderRevenue),
}
